import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';
import LoadingSpinner from '../LoadingSpinner.jsx';

/**
 * Component for managing phone number deletion queue
 */
const DeletionQueuePanel = () => {
    const { getToken } = useAuth();
    const { showToast } = useToast();
    const { theme } = useTheme();

    // State management
    const [deletionRequests, setDeletionRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingRequest, setProcessingRequest] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
    const [rejectionReason, setRejectionReason] = useState('');

    // Statistics
    const [stats, setStats] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        total: 0
    });

    /**
     * Fetch deletion requests from API
     */
    const fetchDeletionRequests = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getToken();
            const response = await apiRequest('/api/admin/phone-numbers/deletion-queue', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response);
                throw new ApiError(errorMessage, response.status, response);
            }

            const data = await response.json();
            setDeletionRequests(data.requests || []);

            // Calculate statistics
            const pending = data.requests?.filter(r => r.status === 'pending').length || 0;
            const approved = data.requests?.filter(r => r.status === 'approved').length || 0;
            const rejected = data.requests?.filter(r => r.status === 'rejected').length || 0;

            setStats({
                pending,
                approved,
                rejected,
                total: data.requests?.length || 0
            });

        } catch (error) {
            console.error('Error fetching deletion requests:', error);
            setError(error.message || 'Failed to load deletion requests');
            showToast('Failed to load deletion requests', 'error');
        } finally {
            setLoading(false);
        }
    }, [getToken, showToast]);

    /**
     * Handle approval/rejection of deletion request
     */
    const handleRequestAction = useCallback(async (requestId, action, reason = null) => {
        try {
            setProcessingRequest(requestId);

            const token = await getToken();
            const response = await apiRequest(`/api/admin/phone-numbers/deletion-queue/${requestId}/${action}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason })
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response);
                throw new ApiError(errorMessage, response.status, response);
            }

            const data = await response.json();

            // Update local state
            setDeletionRequests(prev => prev.map(request =>
                request.id === requestId ? data.request : request
            ));

            showToast(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`, 'success');

            // Refresh stats
            fetchDeletionRequests();

        } catch (error) {
            console.error(`Error ${action}ing request:`, error);
            showToast(error.message || `Failed to ${action} request`, 'error');
        } finally {
            setProcessingRequest(null);
            setShowConfirmModal(false);
            setSelectedRequest(null);
            setActionType(null);
            setRejectionReason('');
        }
    }, [getToken, showToast, fetchDeletionRequests]);

    /**
     * Open confirmation modal
     */
    const openConfirmModal = (request, action) => {
        setSelectedRequest(request);
        setActionType(action);
        setShowConfirmModal(true);
    };

    /**
     * Handle confirmation modal submit
     */
    const handleConfirmSubmit = () => {
        if (selectedRequest && actionType) {
            handleRequestAction(
                selectedRequest.id,
                actionType,
                actionType === 'reject' ? rejectionReason : null
            );
        }
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    /**
     * Get status badge styling
     */
    const getStatusBadge = (status) => {
        const darkStyles = {
            pending: 'bg-yellow-900/20 text-yellow-400',
            approved: 'bg-green-900/20 text-green-400',
            rejected: 'bg-red-900/20 text-red-400',
            completed: 'bg-blue-900/20 text-blue-400'
        };

        const lightStyles = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            completed: 'bg-blue-100 text-blue-800'
        };

        const styles = theme === 'dark' ? darkStyles : lightStyles;
        const defaultStyle = theme === 'dark' ? 'bg-gray-900/20 text-gray-400' : 'bg-gray-100 text-gray-800';

        return `inline-flex px-2 py-1 rounded-full text-xs font-medium ${styles[status] || defaultStyle}`;
    };

    // Load deletion requests on component mount
    useEffect(() => {
        fetchDeletionRequests();
    }, [fetchDeletionRequests]);

    if (loading) {
        return (
            <div className={`rounded-lg p-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                <div className="flex items-center justify-center">
                    <LoadingSpinner />
                    <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading deletion queue...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Phone Deletion Queue</h1>
                    <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Review and manage phone number deletion requests
                    </p>
                </div>
                <button
                    onClick={fetchDeletionRequests}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                        theme === 'dark' 
                            ? 'bg-gray-600 text-white hover:bg-gray-700' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                    <span>🔄</span>
                    <span>Refresh</span>
                </button>
            </div>

            {/* Error state */}
            {error && (
                <div className={`rounded-lg p-4 border ${
                    theme === 'dark' 
                        ? 'bg-red-900/20 border-red-500/50' 
                        : 'bg-red-50 border-red-200'
                }`}>
                    <div className="flex items-center">
                        <span className={`mr-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>⚠️</span>
                        <span className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</span>
                        <button
                            onClick={fetchDeletionRequests}
                            className={`ml-auto underline transition-colors ${
                                theme === 'dark' 
                                    ? 'text-red-400 hover:text-red-300' 
                                    : 'text-red-600 hover:text-red-700'
                            }`}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                    <div className="text-2xl font-bold text-yellow-500">
                        {stats.pending}
                    </div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Pending Review</div>
                </div>
                <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                    <div className="text-2xl font-bold text-green-500">
                        {stats.approved}
                    </div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Approved</div>
                </div>
                <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                    <div className="text-2xl font-bold text-red-500">
                        {stats.rejected}
                    </div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Rejected</div>
                </div>
                <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                    <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {stats.total}
                    </div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Requests</div>
                </div>
            </div>

            {/* Deletion Requests Table */}
            <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                {deletionRequests.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>📋</div>
                        <h3 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>No Deletion Requests</h3>
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            No phone number deletion requests found.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        Phone Number
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        User
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        Reason
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        Requested
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        Status
                                    </th>
                                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                                    }`}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {deletionRequests.map((request) => (
                                    <tr key={request.id} className={`transition-colors ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {request.phone_number}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {request.user_name}
                                            </div>
                                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {request.user_email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm max-w-xs truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {request.reason || 'No reason provided'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                                {formatDate(request.created_at)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={getStatusBadge(request.status)}>
                                                {request.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {request.status === 'pending' && (
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => openConfirmModal(request, 'approve')}
                                                        disabled={processingRequest === request.id}
                                                        className="text-green-400 hover:text-green-300 transition-colors disabled:opacity-50"
                                                        title="Approve deletion"
                                                    >
                                                        ✅
                                                    </button>
                                                    <button
                                                        onClick={() => openConfirmModal(request, 'reject')}
                                                        disabled={processingRequest === request.id}
                                                        className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                                                        title="Reject deletion"
                                                    >
                                                        ❌
                                                    </button>
                                                </div>
                                            )}
                                            {request.status !== 'pending' && (
                                                <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {request.processed_at && `Processed: ${formatDate(request.processed_at)}`}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && selectedRequest && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className={`rounded-lg max-w-md w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className={`flex justify-between items-center p-6 border-b ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {actionType === 'approve' ? 'Approve' : 'Reject'} Deletion Request
                            </h2>
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className={`transition-colors ${
                                    theme === 'dark' 
                                        ? 'text-gray-400 hover:text-white' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Phone Number:</div>
                                <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedRequest.phone_number}</div>
                            </div>

                            <div className="mb-4">
                                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Requested by:</div>
                                <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedRequest.user_name}</div>
                                <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{selectedRequest.user_email}</div>
                            </div>

                            <div className="mb-4">
                                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Reason:</div>
                                <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{selectedRequest.reason || 'No reason provided'}</div>
                            </div>

                            {actionType === 'reject' && (
                                <div className="mb-4">
                                    <label htmlFor="rejection-reason" className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        Rejection Reason (Optional)
                                    </label>
                                    <textarea
                                        id="rejection-reason"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Explain why this request is being rejected..."
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                        }`}
                                        rows={3}
                                    />
                                </div>
                            )}

                            <div className={`p-4 rounded-lg border ${
                                actionType === 'approve'
                                    ? theme === 'dark'
                                        ? 'bg-green-900/20 border-green-500/50'
                                        : 'bg-green-50 border-green-200'
                                    : theme === 'dark'
                                        ? 'bg-red-900/20 border-red-500/50'
                                        : 'bg-red-50 border-red-200'
                            }`}>
                                <div className={`text-sm font-medium mb-2 ${
                                    actionType === 'approve' 
                                        ? theme === 'dark' ? 'text-green-400' : 'text-green-700'
                                        : theme === 'dark' ? 'text-red-400' : 'text-red-700'
                                }`}>
                                    {actionType === 'approve' ? '⚠️ Warning' : '⚠️ Confirmation'}
                                </div>
                                <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {actionType === 'approve'
                                        ? 'Approving this request will permanently delete the phone number from Twilio. This action cannot be undone.'
                                        : 'Are you sure you want to reject this deletion request?'
                                    }
                                </div>
                            </div>
                        </div>

                        <div className={`flex justify-end space-x-3 p-6 border-t ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className={`px-6 py-2 rounded-lg transition-colors ${
                                    theme === 'dark'
                                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmSubmit}
                                disabled={processingRequest === selectedRequest.id}
                                className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
                                    actionType === 'approve'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {processingRequest === selectedRequest.id && <LoadingSpinner size="sm" />}
                                <span>
                                    {processingRequest === selectedRequest.id
                                        ? 'Processing...'
                                        : (actionType === 'approve' ? 'Approve' : 'Reject')
                                    }
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeletionQueuePanel;
